import { useCallback, useEffect, useState } from "react";
import { CheckCheck, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const SEV_STYLE = {
  critical: "bg-red-600",
  high: "bg-amber-500",
  medium: "bg-yellow-400",
  low: "bg-teal-600",
};

export default function NotificationsPage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };
  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  if (!data) return <Skeleton className="h-96 w-full rounded-none" />;

  return (
    <div className="space-y-5 fade-in max-w-4xl" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-muted-foreground">
          <span className="font-mono text-slate-700" data-testid="notifications-unread-label">{data.unread}</span> {t("notifications.unreadOf", { total: data.notifications.length })}
        </p>
        <Button variant="outline" size="sm" onClick={markAll} data-testid="notifications-mark-all-btn"
          className="rounded-none h-8 text-[12px] border-border">
          <CheckCheck className="h-3.5 w-3.5" /> {t("notifications.markAllRead")}
        </Button>
      </div>
      <div className="bg-white border border-border divide-y divide-border">
        {data.notifications.length === 0 && (
          <p className="text-[13px] text-muted-foreground text-center py-10">{t("notifications.empty")}</p>
        )}
        {data.notifications.map((n) => (
          <div key={n.id} data-testid={`notification-row-${n.id}`}
            className={`flex items-start gap-3 px-5 py-4 ${n.read ? "opacity-55" : ""}`}>
            <span className={`h-2.5 w-2.5 mt-1.5 shrink-0 ${SEV_STYLE[n.severity] || "bg-slate-400"}`} />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="text-[13px] font-semibold text-slate-800">{n.title}</p>
                <span className="text-[9px] font-mono uppercase border border-border text-muted-foreground px-1.5 py-0.5">
                  {n.severity}
                </span>
              </div>
              <p className="text-[13px] text-slate-600 mt-0.5">{n.message}</p>
              <p className="text-[11px] font-mono text-muted-foreground mt-1">
                {new Date(n.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
            {!n.read && (
              <Button variant="ghost" size="sm" onClick={() => markRead(n.id)} data-testid={`notification-read-btn-${n.id}`}
                className="rounded-none h-7 text-[11px] text-teal-700">
                <Check className="h-3.5 w-3.5" /> {t("notifications.read")}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
